// eslint-disable-next-line import/no-extraneous-dependencies
import moment from 'moment-timezone-all';
import FundraisingTypeSelector from 'src/components/Control/FundraisingTypeSelector';
import { FORM, FORM_STATUS } from 'src/constants/Form';
import { COLORS } from 'src/constants/Color';
import { FundraisingSections, FundraisingTypes } from 'src/constants/FundraisingTypes';
import { MouseEvent, useEffect, useState } from 'react';
import FormTextField from 'src/components/Control/FormControls/FormTextField';
import {
  FundraisingInfo,
  FundraisingMatchingInfo,
  FundraisingModel,
  Step,
  TABS,
} from 'src/models/FundraisingModel';
import { setError } from 'src/redux/actions/errorsActions';
import { Store, useAppDispatch } from 'src/redux/Store';
import { ErrorBag } from 'src/models/ErrorModel';
import FundraisingInfoValidator from 'src/validations/FundraisingInformationValidator';
import { setAlert } from 'src/redux/actions/alertActions';
import { ALERT_TYPE } from 'src/constants/AlertType';
import { FundraisingProps, renderHasChanged } from 'src/components/Pages/AddFundraisingPage';
import { t } from 'src/lib/language';
import { useSelector } from 'react-redux';
import FundraiserTitle from 'src/components/UI/FundRaiserTitle';
import { makeStyles } from '@material-ui/core/styles';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import FormDateTimePicker from 'src/components/Control/FormControls/FormDateTimePicker';
import FormCheckbox from 'src/components/Control/FormControls/FormCheckbox';
import FundraisingLanguageSelector from 'src/components/Control/FundraisingLanguageSelector';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import BottomRow from 'src/components/UI/BottomRow';
import MetadataSelector, { MetadataType } from 'src/components/Control/MetadataSelector';
import OrganisationBankAccountSelector
  from 'src/components/Control/OrganisationBankAccountSelector';
import GatewaySelector from 'src/components/Control/GatewaySelector';
import { ProcessingType } from 'src/models/GatewayModel';
import ContactGroupSelector from 'src/components/Control/ContactGroupSelector';
import FundraisingMatchingValidator from 'src/validations/FundraisingMatchingValidator';
import { toKebabCase } from 'src/lib/FieldHelper';
import { denormalizeFundraisingSection } from 'src/lib/FundraisingHelper';

const useStyles = makeStyles(() => ({
  row: {
    display: 'flex',
    alignItems: 'start',
    gap: '8px',
    width: '100%',
  },
  columns: {
    display: 'flex',
    flexDirection: 'column',
  },
  textField: {
    width: '148px',
  },
  large: {
    width: '573px',
  },
  col3: {
    display: 'flex',
    width: '32.5%',
  },
  url: {
    fontSize: '12px',
    marginTop: '-4px',
    color: COLORS.darkgrey,
    marginLeft: '2px',
  },
}));

const FundraisingInformationForm = (props: FundraisingProps) => {
  const dispatch = useAppDispatch();
  const classes = useStyles();
  const {
    onChange,
    setErrors,
    fundraisingState,
    setHasChanged,
    hasChanged,
  } = props;

  const [informationState, setInformationState] = useState(
    fundraisingState.fundraisingInfo || {} as FundraisingInfo,
  );
  const [matchingInfoState, setMatchingInfoState] = useState(
    fundraisingState.matchingInfo || {} as FundraisingMatchingInfo,
  );
  const lang = useSelector((state: Store) => state.language.language);

  useEffect(() => {
    dispatch(setError({ [FORM.fundraisingInfo]: {} }));
  }, []);

  useEffect(() => {
    if (!!fundraisingState.fundraisingInfo) {
      setInformationState(fundraisingState.fundraisingInfo);
    }
    if (!!fundraisingState.matchingInfo) {
      setMatchingInfoState(fundraisingState.matchingInfo);
    }
  }, [fundraisingState]);

  const setModified = () => {
    setHasChanged(true);
  };

  const sanitizeInput = (value: string) => {
    let sanitizedString = value.replace(/[^0-9.]/g, '');

    const firstDotIndex = sanitizedString.indexOf('.');
    if (firstDotIndex !== -1) {
      sanitizedString =
        sanitizedString.slice(0, firstDotIndex + 1) +
        sanitizedString.slice(firstDotIndex + 1).replace(/\./g, '');
    }

    const parts = sanitizedString.split('.');
    if (parts.length > 1) {
      const decimalPart = parts[1].substring(0, 2);
      return `${parts[0]}.${decimalPart}`;
    }
    return sanitizedString;
  };

  const onInformationChange = (value: any, field: string) => {
    setModified();
    if (field === 'name') {
      informationState.slug = toKebabCase(value);
    }
    if (field === 'slug') {
      setInformationState({
        ...informationState,
        [field]: toKebabCase(value),
      });
      return;
    }
    if (field === 'button_labels') {
      const currentLabel = (typeof informationState.button_labels === 'object'
        && informationState.button_labels !== null)
        ? { ...informationState.button_labels }
        : { en: '', fr: '' };
      setInformationState({
        ...informationState,
        button_labels: { ...currentLabel, ...value },
      });
      return;
    }
    setInformationState({
      ...informationState,
      [field]: value,
    });
  };

  const onMatchingInfoChange = (value: any, field: string) => {
    setModified();
    if (['raised', 'goal', 'bonus_round_road', 'bonus_round_multiplier'].includes(field)) {
      setMatchingInfoState({
        ...matchingInfoState,
        [field]: sanitizeInput(value),
      });
    }
    setMatchingInfoState({
      ...matchingInfoState,
      [field]: value,
    });
  };

  const handleSubmit = (event: MouseEvent<HTMLElement>, advance: boolean) => {
    event.preventDefault();
    dispatch(setError({ [FORM.fundraisingInfo]: {} }));
    setErrors(TABS.information, false);
    const errorBagInfo = {} as ErrorBag;
    const errorBagMatching = {} as ErrorBag;
    let hasErrors = false;
    try {
      FundraisingInfoValidator().validateSync(informationState, { abortEarly: false });
    } catch (validationErrors: any) {
      hasErrors = true;
      validationErrors.inner.forEach((e: any) => {
        if (!errorBagInfo[e.path]) {
          errorBagInfo[e.path] = [];
        }
        errorBagInfo[e.path].push(e.message);
      });
    }
    if (informationState.type === FundraisingTypes.matching) {
      try {
        FundraisingMatchingValidator()
          .validateSync(matchingInfoState, { abortEarly: false });
      } catch (validationErrors: any) {
        hasErrors = true;
        validationErrors.inner.forEach((e: any) => {
          if (!errorBagMatching[e.path]) {
            errorBagMatching[e.path] = [];
          }
          errorBagMatching[e.path].push(e.message);
        });
      }
    }
    if (hasErrors) {
      dispatch(setAlert({
        type: ALERT_TYPE.error,
        message: 'Validations Error',
      }));
      const errorBag = { ...errorBagInfo, ...errorBagMatching };
      dispatch(setError({ [FORM.fundraisingInfo]: errorBag }));
      setErrors(TABS.information, true);
      return;
    }
    setHasChanged(false);
    const payloadInfo = denormalizeFundraisingSection(
      FundraisingSections.info, informationState,
    );
    const payloadMatching = denormalizeFundraisingSection(
      FundraisingSections.matching, matchingInfoState,
    );
    onChange(
      {
        ...fundraisingState,
        fundraisingInfo: {
          ...payloadInfo,
        },
        matchingInfo: payloadMatching,
      } as FundraisingModel,
      Step.info,
      advance,
    );
  };

  const getURL = () => {
    if (informationState.type !== FundraisingTypes.matching) {
      return `https://www.perfectdeed.ca/events/${informationState.slug ?? ''}`;
    }
    return `https://www.perfectdeed.ca/${informationState.slug ?? ''}`;
  };

  const getLabel = (disabled: boolean, label: string) => {
    if (disabled) { return (<span style={{ color: '#ccc' }}><i>{label}</i></span>); }
    return (<span style={{ color: '#666' }}>{label}</span>);
  };

  const copySlugURL = () => {
    navigator.clipboard.writeText(getURL());
  };

  const formStatus = useSelector((state: Store) => state.formStatus);
  const processing = () => formStatus[FORM.fundraising] === FORM_STATUS.processing;

  const disabled = processing();

  const getButtonLabelValue = (language: 'en' | 'fr'): string => {
    const label = informationState.button_labels;
    if (typeof label === 'object' && label !== null) {
      return label[language] || '';
    }
    return '';
  };

  return (
    <div>
      <FundraiserTitle>General Informations</FundraiserTitle>
      <form noValidate>
        <div className={classes.row}>
          <div className={classes.columns}>
            <div style={{ width: '116px', marginTop: '16px' }}>
              <FundraisingTypeSelector
                name="type"
                form={FORM.fundraisingInfo}
                onChange={onInformationChange}
                value={informationState.type}
                disabled={disabled || !!informationState.id} />
            </div>
          </div>
          <div className={classes.columns}>
            <div style={{ width: '467px', marginTop: '16px' }}>
              <FormTextField
                form={FORM.fundraisingInfo}
                label={t(lang, 'forms.fundraising.name')}
                name="name"
                onChange={onInformationChange}
                value={informationState.name}
                margin="dense" />
            </div>
          </div>
          <div className={classes.columns}>
            <div style={{ width: '140px' }}>
              <FormDateTimePicker
                form={FORM.fundraisingInfo}
                label={t(lang, 'forms.fundraising.start_date')}
                name="start_date"
                onChange={
                  (value: MaterialUiPickersDate) => onInformationChange(value, 'start_date')
                }
                value={informationState.start_date || moment().add(1, 'day').startOf('day')}
                minDate={moment().add(1, 'day').startOf('day')}
                showTime
                required />
            </div>
          </div>
          <div className={classes.columns}>
            <div style={{ width: '140px' }}>
              <FormDateTimePicker
                form={FORM.fundraisingInfo}
                label={t(lang, 'forms.fundraising.end_date')}
                name="end_date"
                onChange={
                  (value: MaterialUiPickersDate) => onInformationChange(value, 'end_date')
                }
                value={informationState.end_date || moment()}
                style={{ width: '140xp' }}
                showTime
                required />
            </div>
          </div>
          <div className={classes.columns}>
            <div style={{ width: '144px', marginTop: '32px', marginLeft: '8px' }}>
              <FormCheckbox
                label="Has end"
                name="has_end"
                onChange={onInformationChange}
                checked={!!informationState.has_end} />
            </div>
          </div>
        </div>
        <div className={classes.row} style={{ marginTop: '-16px' }}>
          <div className={classes.columns}>
            <FormCheckbox
              style={{ marginTop: '24px', width: '112px' }}
              label="Active"
              name="active"
              onChange={(value: boolean) => { onInformationChange(value, 'active'); }}
              checked={!!informationState.active} />
          </div>
          <div className={classes.columns}>
            <div className={classes.textField} style={{ marginTop: '16px', width: '236px' }}>
              <FundraisingLanguageSelector
                styles={{ minWidth: 0, width: '100%' }}
                form={FORM.fundraisingInfo}
                name="default_language"
                onChange={onInformationChange}
                value={informationState.default_language}
                label="Default Language" />
            </div>
          </div>
          <div className={classes.columns}>
            <div style={{ marginTop: '16px', width: '517px', marginLeft: '2px' }}>
              <FormTextField
                form={FORM.fundraisingInfo}
                label={t(lang, 'forms.fundraising.slug')}
                name="slug"
                onChange={onInformationChange}
                margin="dense"
                value={informationState.slug} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <div className={classes.url}>{getURL()}</div>
              <Tooltip title="Copy link">
                <IconButton size="small" onClick={copySlugURL}>
                  <ContentCopyIcon style={{ fontSize: '14px' }} />
                </IconButton>
              </Tooltip>
            </div>
          </div>
        </div>
        <div className={classes.row} style={{ marginTop: '-12px' }}>
          <div className={classes.columns} style={{ flexDirection: 'row', width: '344px' }}>
            <div style={{ width: '301px' }}>
              <FormCheckbox
                style={{ marginTop: '24px', marginLeft: '-12px' }}
                label="Show countdown"
                name="show_countdown"
                onChange={onInformationChange}
                checked={!!informationState.show_countdown} />
            </div>
            <div>
              <FormDateTimePicker
                form={FORM.fundraisingInfo}
                label="Countdown end"
                name="countdown_end_date"
                onChange={
                  (value: MaterialUiPickersDate) => onInformationChange(value, 'countdown_end_date')
                }
                value={informationState.countdown_end_date || moment()}
                showTime
                required />
            </div>
          </div>
          <div className={classes.columns} style={{ flexDirection: 'row', width: '344px' }}>
            <div style={{ width: '57%' }}>
              <FormCheckbox
                style={{ marginTop: '24px' }}
                label="Ask for admin fees"
                name="ask_for_fee"
                onChange={onInformationChange}
                checked={!!informationState.ask_for_fee} />
            </div>
            <div className={classes.textField} style={{ marginTop: '16px' }}>
              <FormTextField
                form={FORM.fundraisingInfo}
                label={getLabel(!informationState.ask_for_fee, 'Fee %')}
                name="admin_fee"
                onChange={onInformationChange}
                value={informationState.admin_fee}
                margin="dense"
                disabled={!informationState.ask_for_fee} />
            </div>
          </div>
          <div className={classes.columns} style={{ flexDirection: 'row', width: '344px' }}>
            <div style={{ width: '57%' }}>
              <FormCheckbox
                style={{ marginTop: '18px' }}
                label="Show suggested"
                name="show_suggested"
                onChange={onInformationChange}
                checked={!!informationState.show_suggested} />
            </div>
            <div className={classes.textField} style={{ marginTop: '16px' }}>
              <FormTextField
                form={FORM.fundraisingInfo}
                label={getLabel(!informationState.show_suggested, 'Suggested')}
                name="max_amount"
                onChange={onInformationChange}
                value={informationState.max_amount}
                margin="dense"
                disabled={!informationState.show_suggested} />
            </div>
          </div>
        </div>
        { informationState.type === FundraisingTypes.matching && (
        <div className={classes.row} style={{ marginTop: '-8px' }}>
          <div className={classes.columns} style={{ flexDirection: 'row', width: '344px' }}>
            <div style={{ width: '57%' }}>
              <FormCheckbox
                style={{ marginTop: '8px', width: '203px' }}
                label="Bonus round active"
                name="bonus_round_active"
                onChange={(value: boolean) => { onMatchingInfoChange(value, 'bonus_round_active'); }}
                checked={!!matchingInfoState.bonus_round_active} />
            </div>
            <div className={classes.textField}>
              <FormTextField
                form={FORM.fundraisingInfo}
                label={getLabel(!matchingInfoState.bonus_round_active, 'Bonus round goal')}
                name="bonus_round_goal"
                onChange={onMatchingInfoChange}
                value={matchingInfoState.bonus_round_goal}
                margin="dense"
                disabled={!matchingInfoState.bonus_round_active} />
            </div>
          </div>
          <div className={classes.columns} style={{ flexDirection: 'row', width: '344px' }}>
            <div style={{ width: '57%' }}>
              <FormCheckbox
                style={{ marginTop: '8px', width: '203px' }}
                label="Display goal"
                name="show_goal"
                onChange={(value: boolean) => { onMatchingInfoChange(value, 'show_goal'); }}
                checked={!!matchingInfoState.show_goal} />
            </div>
            <div className={classes.textField}>
              <FormTextField
                form={FORM.fundraisingInfo}
                label={getLabel(!matchingInfoState.show_goal, 'Goal')}
                name="goal"
                onChange={onMatchingInfoChange}
                value={matchingInfoState.goal}
                margin="dense"
                disabled={!matchingInfoState.show_goal} />
            </div>
          </div>
          <div className={classes.columns} style={{ flexDirection: 'row', width: '344px' }}>
            <div style={{ width: '57%' }}>
              <FormCheckbox
                style={{ marginTop: '8px', width: '203px' }}
                label="Display raised amount"
                name="en"
                onChange={(value: boolean) => {
                  onMatchingInfoChange(value, 'show_raised_amount');
                }}
                checked={!!matchingInfoState.show_raised_amount} />
            </div>
          </div>
        </div>
        )}
        { informationState.type === FundraisingTypes.matching && (
          <div className={classes.row} style={{ marginTop: '0px' }}>
            <div className={classes.columns} style={{ flexDirection: 'row', width: '538px' }}>
              <div style={{ width: '196px' }}>
                <FormCheckbox
                  style={{ marginTop: '8px', width: '203px' }}
                  label="Matching active"
                  name="active_matching"
                  onChange={(value: boolean) => { onMatchingInfoChange(value, 'active_matching'); }}
                  checked={!!matchingInfoState.active_matching} />
              </div>
              <div className={classes.row} style={{ width: '296px' }}>
                <div style={{ width: '33%' }}>
                  <FormTextField
                    form={FORM.fundraisingInfo}
                    label={getLabel(!matchingInfoState.matching_multiplier, 'Multiplier')}
                    name="matching_multiplier"
                    onChange={onMatchingInfoChange}
                    value={matchingInfoState.matching_multiplier}
                    margin="dense"
                    disabled={!matchingInfoState.active_matching} />
                </div>
                <div style={{ width: '33%' }}>
                  <FormTextField
                    form={FORM.fundraisingInfo}
                    label={getLabel(!matchingInfoState.active_matching, 'Start at')}
                    name="start_amount_multiplier"
                    onChange={onMatchingInfoChange}
                    value={matchingInfoState.start_amount_multiplier}
                    margin="dense"
                    disabled={!matchingInfoState.active_matching} />
                </div>
                <div style={{ width: '33%' }}>
                  <FormTextField
                    form={FORM.fundraisingInfo}
                    label={getLabel(!matchingInfoState.active_matching, 'End at')}
                    name="end_amount_multiplier"
                    onChange={onMatchingInfoChange}
                    value={matchingInfoState.end_amount_multiplier}
                    margin="dense"
                    disabled={!matchingInfoState.active_matching} />
                </div>
              </div>
            </div>
            <div className={classes.columns} style={{ flexDirection: 'row', width: '344px' }}>
              &nbsp;
            </div>
          </div>
        )}
        <div className={classes.row}>
          <div className={classes.columns} style={{ flexDirection: 'row', width: '344px' }}>
            <MetadataSelector
              id="program-selector"
              type={MetadataType.program}
              onChange={(id) => onInformationChange(id, 'program_id')}
              form={FORM.fundraisingInfo}
              name="program_id"
              value={informationState.program_id || undefined}
              margin="dense"
              noMinWidth
              noMarginTop />
          </div>
          <div className={classes.columns} style={{ flexDirection: 'row', width: '344px' }}>
            <MetadataSelector
              id="department-selector"
              type={MetadataType.department}
              onChange={(id) => onInformationChange(id, 'department_id')}
              form={FORM.fundraisingInfo}
              name="department_id"
              value={informationState.department_id || undefined}
              margin="dense"
              noMinWidth
              noMarginTop />
          </div>
          <div className={classes.columns} style={{ flexDirection: 'row', width: '344px' }}>
            <MetadataSelector
              id="account-selector"
              type={MetadataType.account}
              onChange={(id) => onInformationChange(id, 'account_id')}
              form={FORM.fundraisingInfo}
              name="account_id"
              value={informationState.account_id || undefined}
              margin="dense"
              noMinWidth
              noMarginTop />
          </div>
        </div>
        <div className={classes.row} style={{ marginTop: '-12px' }}>
          <div className={classes.columns} style={{ flexDirection: 'row', width: '344px' }}>
            <OrganisationBankAccountSelector
              value={informationState.bank_account_id || undefined}
              onChange={(id:number) => onInformationChange(id, 'bank_account_id')}
              form={FORM.fundraisingInfo}
              name="bank_account_id"
              margin="dense"
              noMinWidth
              noMarginTop />
          </div>
          <div className={classes.columns} style={{ flexDirection: 'row', width: '344px' }}>
            <GatewaySelector
              id="gateway-cc"
              form={FORM.fundraisingInfo}
              name="payment_gateway_id"
              onChange={(value) => onInformationChange(value, 'payment_gateway_id')}
              value={informationState.payment_gateway_id || 0}
              type={ProcessingType.cc}
              margin="dense"
              noMinWidth
              noMarginTop />
          </div>
          <div className={classes.columns} style={{ flexDirection: 'row', width: '344px' }}>
            <div style={{ width: '57%' }}>
              <FormCheckbox
                style={{ marginTop: '8px', width: '203px' }}
                label="Allow installment"
                name="allow_installment"
                onChange={(value: boolean) => { onInformationChange(value, 'allow_installment'); }}
                checked={!!informationState.allow_installment} />
            </div>
            <div className={classes.textField}>
              <FormTextField
                form={FORM.fundraisingInfo}
                label={getLabel(!informationState.allow_installment, 'Max Installment')}
                name="max_installments"
                onChange={onInformationChange}
                value={informationState.max_installments}
                margin="dense"
                disabled={!informationState.allow_installment} />
            </div>
          </div>
        </div>
        <FundraiserTitle>Donate buttons label</FundraiserTitle>
        <div className={classes.row} style={{ marginBottom: '16px' }}>
          <div className={classes.columns} style={{ width: '344px' }}>
            <FormTextField
              form={FORM.fundraisingInfo}
              label="French Label"
              name="button_labels_fr"
              onChange={(value: string) => onInformationChange({ fr: value }, 'button_labels')}
              value={getButtonLabelValue('fr')}
              margin="dense" />
          </div>
          <div className={classes.columns} style={{ width: '344px' }}>
            <FormTextField
              form={FORM.fundraisingInfo}
              label="English Label"
              name="button_labels_en"
              onChange={(value: string) => onInformationChange({ en: value }, 'button_labels')}
              value={getButtonLabelValue('en')}
              margin="dense" />
          </div>
        </div>
        <div className={classes.row} style={{ marginTop: '-16px' }}>
          <div className={classes.columns}>
            <ContactGroupSelector
              disabled={disabled}
              onChange={(value: number[]) => onInformationChange(value, 'groups_id')}
              values={informationState.groups_id} />
          </div>
        </div>
        <BottomRow>
          <Button
            variant="contained"
            color="primary"
            onClick={(e) => handleSubmit(e, false)}
            key="submit-button">
            Save{renderHasChanged(!hasChanged)}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={(e) => handleSubmit(e, true)}
            key="submit-button-next">
            Save{renderHasChanged(!hasChanged)} and Next Step
          </Button>
        </BottomRow>
      </form>
    </div>
  );
};

export default FundraisingInformationForm;
